import { CSSProperties, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { DataTable } from "../../components/DataTable/DataTable";
import { Ruling } from "../../components/Ruling";
import { Check } from "../../components/Check/Check";
import { Row } from "../../components/Row";
import { GamePlayer } from "../../types/generalTypes";
import { shuffle } from "../../util/generalUtil";
import { useAppSelector } from "../../state/store";
import { VerseType } from "../../types/practiceTypes";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

export function BibleGame() {
  useDocumentTitle("The Bible Game - Bible Quiz Academy");
  const location = useLocation();
  const settings = useAppSelector((state) => state.settings);
  const { material } = useAppSelector((state) => state.material);
  const innings = 9;
  const mode: "SELECTED" | "ALL" =
    location.state?.bibleGame?.settings?.mode || "SELECTED";
  const settingsPlayers = (location.state?.bibleGame?.players ||
    []) as GamePlayer[];
  const [players, setPlayers] = useState(
    shuffle(
      settingsPlayers.map((player) => ({
        name: player.name,
        score: Array(innings).fill(0) as number[],
        material: player.material,
        bk: 0,
        ch: 0,
        vs: 0,
      }))
    )
  );
  const [currentInning, setCurrentInning] = useState(0);
  const [turn, setTurn] = useState(0);
  const [phase, setPhase] = useState<
    "READY" | "ANSWERING" | "RULING" | "FINISHED"
  >("READY");
  const [currentVerse, setCurrentVerse] = useState<VerseType>();
  const [refCorrect, setRefCorrect] = useState({
    book: 0,
    chapter: 0,
    verse: 0,
  });

  const makeRuling = (ruling: number) => {
    if (ruling === 2) {
      // Throwout
      setPhase("READY");
      return;
    }
    // Assign verse status
    const newPlayers = [...players];
    const thisPlayer = {
      ...newPlayers[turn],
      score: [...newPlayers[turn].score],
    };
    let score = 0;
    if (mode === "ALL") {
      if (refCorrect.book) {
        thisPlayer.bk++;
        score++;
      }
      if (refCorrect.chapter) {
        thisPlayer.ch++;
        score++;
      }
      if (refCorrect.verse) {
        thisPlayer.vs++;
        score++;
      }
    } else {
      if (ruling === 0) score = 1;
    }
    thisPlayer.score[currentInning] = ruling === 0 ? score : 0;
    newPlayers[turn] = thisPlayer;
    setPlayers(newPlayers);

    if (turn + 1 === players.length) {
      if (currentInning + 1 === innings) {
        setPhase("FINISHED");
        setTurn(-1);
        setCurrentInning(9999);
      } else {
        setPhase("READY");
        setTurn(0);
        setCurrentInning(currentInning + 1);
      }
    } else {
      setPhase("READY");
      setTurn(turn + 1);
    }
  };

  if (players.length === 0) {
    return <Navigate to="/" />;
  }

  return (
    <div className="page">
      <Row>
        <Link className="secondary-btn" to="/">
          Go Back
        </Link>
        <h1 style={{ marginLeft: 10 }}>The Bible Game</h1>
      </Row>
      <DataTable
        style={{ maxWidth: 800 }}
        data={players}
        rowDisplay={(_, i) => ({ selected: i === turn })}
        columns={[
          {
            title: "Name",
            property: "name",
            render: (p) => p.name,
            contentStyle: { minWidth: 110 },
          },
          ...players[0].score.map((_, i) => ({
            title: String(i + 1),
            property: String(i),
            render: (p: (typeof players)[0], playerIndex: number) =>
              i > currentInning || (i === currentInning && playerIndex >= turn)
                ? ""
                : p.score[i],
            contentStyle: { textAlign: "center" } as CSSProperties,
          })),
          {
            title: "Bk",
            property: "bk",
            render: (p) => p.bk,
            visible: mode === "ALL",
            contentStyle: { textAlign: "center" },
          },
          {
            title: "Ch",
            property: "ch",
            render: (p) => p.ch,
            visible: mode === "ALL",
            contentStyle: { textAlign: "center" },
          },
          {
            title: "Vs",
            property: "vs",
            render: (p) => p.vs,
            visible: mode === "ALL",
            contentStyle: { textAlign: "center" },
          },
          {
            title: "Total",
            property: "total",
            render: (p) => p.score.reduce((total, value) => total + value, 0),
            contentStyle: { textAlign: "center", fontWeight: "bold" },
          },
        ]}
      />
      {phase !== "FINISHED" && (
        <div style={{ marginTop: 30 }}>
          {phase === "READY" && (
            <button
              className="clickable"
              key="next-verse"
              style={{ marginTop: 40 }}
              onClick={() => {
                setPhase("ANSWERING");

                // Pick a random verse from the player's material
                const allVerses: VerseType[] = [];
                material.forEach((book, index) => {
                  book.chapters.forEach((chapter, chapterIndex) => {
                    const chapters =
                      players[turn].material || settings.selectedChapters;
                    if (
                      (mode === "ALL" && index < 27) ||
                      (chapters &&
                        chapters[index] &&
                        chapters[index][chapterIndex])
                    ) {
                      chapter.forEach((verse, verseIndex) => {
                        const chapterFromSettings =
                          chapters[index][chapterIndex];
                        const chapterSettings =
                          typeof chapterFromSettings === "boolean"
                            ? Array(chapter.length).fill(chapterFromSettings)
                            : chapterFromSettings;
                        if (
                          (mode === "ALL" || chapterSettings[verseIndex]) &&
                          verse.verse !== "No Verse"
                        ) {
                          allVerses.push({
                            reference: `${book.bookName} ${chapterIndex + 1}:${
                              verseIndex + 1
                            }`,
                            book: index,
                            chapter: chapterIndex,
                            verseNum: verseIndex,
                            verse: verse.verse,
                            questions: [],
                          });
                        }
                      });
                    }
                  });
                });
                shuffle(allVerses);
                setCurrentVerse(allVerses[0]);
              }}
            >
              Ask Verse
            </button>
          )}
          {phase === "ANSWERING" && (
            <>
              <p style={{ fontSize: 16 }}>{currentVerse?.verse}</p>
              <button
                className="clickable"
                style={{ marginTop: 10 }}
                onClick={() => {
                  setPhase("RULING");
                  setRefCorrect({ book: 0, chapter: 0, verse: 0 });
                }}
              >
                Show Reference
              </button>
            </>
          )}
          {phase === "RULING" && (
            <>
              <p style={{ fontSize: 16 }}>{currentVerse?.verse}</p>
              <p style={{ fontWeight: "bold" }}>{currentVerse?.reference}</p>
              {mode === "SELECTED" && <Ruling onClick={makeRuling} />}
              {mode === "ALL" && (
                <>
                  <p style={{ marginTop: 30 }}>What did you get correct?</p>
                  <Row>
                    <Check
                      style={{ width: 100 }}
                      checked={refCorrect.book === 1}
                      onClick={(checked) =>
                        setRefCorrect({ ...refCorrect, book: checked ? 1 : 0 })
                      }
                    >
                      Book
                    </Check>
                    <Check
                      style={{ width: 100, marginLeft: 10 }}
                      checked={refCorrect.chapter === 1}
                      onClick={(checked) =>
                        setRefCorrect({
                          ...refCorrect,
                          chapter: checked ? 1 : 0,
                        })
                      }
                    >
                      Chapter
                    </Check>
                    <Check
                      style={{ width: 100, marginLeft: 10 }}
                      checked={refCorrect.verse === 1}
                      onClick={(checked) =>
                        setRefCorrect({ ...refCorrect, verse: checked ? 1 : 0 })
                      }
                    >
                      Verse
                    </Check>
                  </Row>
                  <Row style={{ marginTop: 10 }}>
                    <button className="clickable" onClick={() => makeRuling(0)}>
                      Submit
                    </button>
                    <button
                      className="link"
                      onClick={() => makeRuling(2)}
                      style={{ marginLeft: 15 }}
                    >
                      Throwout
                    </button>
                  </Row>
                </>
              )}
            </>
          )}
        </div>
      )}
      {phase === "FINISHED" && (
        <p style={{ fontSize: 18, marginTop: 40 }}>Game Over!</p>
      )}
    </div>
  );
}
